import * as React from 'react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import moment from 'moment-timezone'
import { sleep } from '@/utils/sleep'
import { Card } from '@/components/ui/card'
import { fError } from '@/utils/format-error'
import axios, { endpoints } from '@/lib/axios'
import { fNumber } from '@/utils/format-number'
import { Button } from '@/components/ui/button'
import { useBoolean } from '@/hooks/use-boolean'
import { ChartContainer } from '@/components/ui/chart'
import { useDeepEffect } from '@/hooks/use-deep-effect'
import { useOnlineUserCount } from '@/hooks/use-online-user-count'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import LoadingSection from '@/components/loading-indicator/loading-section'
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Users,
  TrendingUp,
  Activity,
  Play,
  CalendarIcon,
  Radio,
} from 'lucide-react'

import type {
  GetDashboardSummaryResponse,
  // GetMonthlyStatsResponse,
  GetRegisterCountResponse,
  GetLoginCountResponse,
} from './types'

interface SummaryBoxProps {
  label: string
  value: string
  icon?: React.ReactNode
  trend?: {
    value: number
    isPositive: boolean
  }
}

function SummaryBox({ label, value, icon, trend }: SummaryBoxProps) {
  return (
    <Card className="flex flex-col gap-2 p-4 sm:p-5 w-full h-full justify-between min-h-[120px]">
      <div className="flex items-center gap-2 text-muted-foreground text-xs sm:text-sm font-medium">
        <div className="flex-shrink-0">{icon}</div>
        <span className="line-clamp-2 leading-tight">{label}</span>
      </div>
      <div className="text-xl sm:text-2xl font-bold text-black dark:text-white mt-1">
        {value}
      </div>
      {trend && (
        <div
          className={`text-xs sm:text-sm ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}
        >
          {trend.isPositive ? '+' : ''}
          {trend.value}%
        </div>
      )}
    </Card>
  )
}

export default function DashboardAdminView() {
  const onlineUserCount = useOnlineUserCount()
  const isFetching = useBoolean(true)
  const [dateRange, setDateRange] = React.useState<{
    from: Date | undefined
    to: Date | undefined
  }>({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date(),
  })
  const [chartType, setChartType] = React.useState<'online' | 'new'>('new')
  const [summary, setSummary] =
    React.useState<GetDashboardSummaryResponse | null>(null)

  // const [monthlyStats, setMonthlyStats] =
  //   React.useState<GetMonthlyStatsResponse | null>(null)
  const [registerCount, setRegisterCount] =
    React.useState<GetRegisterCountResponse | null>(null)
  const [loginCount, setLoginCount] =
    React.useState<GetLoginCountResponse | null>(null)

  // Helper function to get proper end date in Thailand timezone (+7 hours)
  const getEndDate = (date: Date | undefined) => {
    if (!date) {
      // Current time in Thailand timezone (+7 hours) converted to UTC ISO string
      return moment().tz('Asia/Bangkok').add(7, 'hours').toISOString()
    }

    const today = moment().tz('Asia/Bangkok')
    const selectedDate = moment(date).tz('Asia/Bangkok')
    const isToday = selectedDate.isSame(today, 'day')

    if (isToday) {
      // Current time in Thailand timezone (+7 hours) converted to UTC ISO string
      return moment().tz('Asia/Bangkok').add(7, 'hours').toISOString()
    } else {
      // End of the selected date in Thailand timezone (+7 hours) converted to UTC ISO string
      return selectedDate.endOf('day').add(7, 'hours').toISOString()
    }
  }

  // Helper function to get start date in Thailand timezone (+7 hours)
  const getStartDate = (date: Date | undefined) => {
    if (!date) {
      // Start of current month in Thailand timezone (+7 hours) converted to UTC ISO string
      return moment()
        .tz('Asia/Bangkok')
        .startOf('month')
        .add(7, 'hours')
        .toISOString()
    }

    // Start of the selected date in Thailand timezone (+7 hours) converted to UTC ISO string
    return moment(date)
      .tz('Asia/Bangkok')
      .startOf('day')
      .add(7, 'hours')
      .toISOString()
  }

  useDeepEffect(() => {
    const fetchDashboard = async () => {
      try {
        isFetching.onTrue()

        // Fetch all dashboard data in parallel
        const [
          summaryData,
          // monthlyStatsData,
          registerCountData,
          loginCountData,
        ] = await Promise.all([
          axios.get<GetDashboardSummaryResponse>(endpoints.dashboard.summary),
          // axios.get<GetMonthlyStatsResponse>(endpoints.dashboard.monthlyStats),
          axios.get<GetRegisterCountResponse>(
            endpoints.dashboard.registerCount({
              startDate: getStartDate(dateRange.from),
              endDate: getEndDate(dateRange.to),
              groupBy: 'day',
            }),
          ),
          axios.get<GetLoginCountResponse>(
            endpoints.dashboard.loginCount({
              startDate: getStartDate(dateRange.from),
              endDate: getEndDate(dateRange.to),
              groupBy: 'day',
            }),
          ),
        ])

        setSummary(summaryData.data)
        // setMonthlyStats(monthlyStatsData.data)
        setRegisterCount(registerCountData.data)
        setLoginCount(loginCountData.data)
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
        toast.error(fError(error))
      } finally {
        await sleep(300) // Simulate loading time
        isFetching.onFalse()
      }
    }

    fetchDashboard()
  }, [])

  // Prepare summary data
  const summaryData: SummaryBoxProps[] = [
    {
      label: 'ผู้ใช้ทั้งหมด',
      value: summary?.totalUsers?.toLocaleString() || '0',
      icon: <Users className="h-4 w-4" />,
    },
    {
      label: 'ผู้ใช้ที่ออนไลน์ขณะนี้',
      value: fNumber(onlineUserCount),
      icon: <Activity className="h-4 w-4" />,
    },
    {
      label: 'โปรแกรมทั้งหมด',
      value: summary?.totalPrograms?.toLocaleString() || '0',
      icon: <Play className="h-4 w-4" />,
    },
    {
      label: 'โปรแกรมที่กำลังถ่ายทอด',
      value: summary?.totalLivePrograms?.toLocaleString() || '0',
      icon: <Radio className="h-4 w-4" />,
    },
  ]

  // Refetch data when date range or chart type changes
  useDeepEffect(() => {
    const fetchChartData = async () => {
      if (dateRange.from && dateRange.to) {
        try {
          if (chartType === 'new') {
            const response = await axios.get<GetRegisterCountResponse>(
              endpoints.dashboard.registerCount({
                startDate: getStartDate(dateRange.from),
                endDate: getEndDate(dateRange.to),
                groupBy: 'day',
              }),
            )
            setRegisterCount(response.data)
          } else {
            const response = await axios.get<GetLoginCountResponse>(
              endpoints.dashboard.loginCount({
                startDate: getStartDate(dateRange.from),
                endDate: getEndDate(dateRange.to),
                groupBy: 'day',
              }),
            )
            setLoginCount(response.data)
          }
        } catch (error) {
          console.error('Error fetching chart data:', error)
          toast.error(fError(error))
        }
      }
    }

    fetchChartData()
  }, [dateRange, chartType])

  // Prepare chart data based on chart type
  const chartData =
    chartType === 'new'
      ? registerCount?.data?.map((item) => ({
          month: item.label,
          value: item.count,
        })) ||
        // monthlyStats?.monthlyUsers?.map((item) => ({
        //   month: item.month,
        //   value: item.count || 0,
        // })) ||
        []
      : loginCount?.data?.map((item) => ({
          month: item.label,
          value: item.count,
        })) ||
        // monthlyStats?.monthlyUsers?.map((item) => ({
        //   month: item.month,
        //   value: item.count || 0,
        // })) ||
        []

  return (
    <div className="w-full space-y-4 sm:space-y-6 p-3 sm:p-4 lg:p-6">
      <LoadingSection loading={isFetching.value}>
        {/* Chart Section */}
        <Card className="w-full p-4 sm:p-6 flex flex-col">
          <div className="flex flex-col space-y-4 mb-4 sm:mb-6">
            {/* Header */}
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              <h2 className="font-semibold text-base sm:text-lg">
                สถิติผู้ใช้งาน
              </h2>
            </div>

            {/* Controls */}
            <div className="flex flex-col space-y-3 sm:space-y-4">
              {/* Chart Type Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button
                    variant={chartType === 'new' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setChartType('new')}
                    className="flex-1 sm:flex-none text-xs sm:text-sm"
                  >
                    ผู้ใช้ใหม่
                  </Button>
                  <Button
                    variant={chartType === 'online' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setChartType('online')}
                    className="flex-1 sm:flex-none text-xs sm:text-sm"
                  >
                    ผู้ใช้ออนไลน์
                  </Button>
                </div>

                {/* Date Range Filter */}
                <div className="w-full sm:w-auto">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="date"
                        variant="outline"
                        size="sm"
                        className={cn(
                          'w-full sm:w-auto justify-center sm:justify-start text-left font-normal min-w-0',
                          !dateRange.from && 'text-muted-foreground',
                        )}
                      >
                        <CalendarIcon className="mr-2 h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="truncate text-xs sm:text-sm">
                          {dateRange?.from ? (
                            dateRange.to ? (
                              <span className="hidden sm:inline">
                                {format(dateRange.from, 'dd/MM/yyyy')} -{' '}
                                {format(dateRange.to, 'dd/MM/yyyy')}
                              </span>
                            ) : (
                              format(dateRange.from, 'dd/MM/yyyy')
                            )
                          ) : (
                            'เลือกช่วงวันที่'
                          )}
                          {/* Mobile: Show short format */}
                          {dateRange?.from && dateRange.to && (
                            <span className="sm:hidden">
                              {format(dateRange.from, 'dd/MM')} -{' '}
                              {format(dateRange.to, 'dd/MM')}
                            </span>
                          )}
                        </span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0"
                      align="start"
                      sideOffset={4}
                    >
                      <CalendarComponent
                        autoFocus
                        mode="range"
                        defaultMonth={dateRange?.from}
                        selected={dateRange}
                        onSelect={(range) =>
                          setDateRange({
                            from: range?.from || undefined,
                            to: range?.to || undefined,
                          })
                        }
                        numberOfMonths={window.innerWidth < 768 ? 1 : 2}
                        className="w-full"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </div>

          {/* Chart Container */}
          <div className="w-full overflow-x-auto">
            <ChartContainer
              config={{ value: { color: '#000', label: 'ผู้ใช้' } }}
              className="w-full min-w-[300px] h-64 sm:h-80 lg:h-96"
            >
              <BarChart
                data={chartData}
                margin={{
                  top: 10,
                  right: 10,
                  left: 0,
                  bottom: 10,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }}
                  interval="preserveStartEnd"
                  angle={window.innerWidth < 640 ? -45 : 0}
                  textAnchor={window.innerWidth < 640 ? 'end' : 'middle'}
                  height={window.innerWidth < 640 ? 60 : 40}
                />
                <YAxis
                  tick={{ fontSize: window.innerWidth < 640 ? 10 : 12 }}
                  width={window.innerWidth < 640 ? 40 : 50}
                />
                <Tooltip
                  formatter={(value: number) => [
                    `${value.toLocaleString()} คน`,
                    chartType === 'new' ? 'ผู้ใช้ใหม่' : 'ผู้ใช้ออนไลน์',
                  ]}
                  labelFormatter={(label) => `${label}`}
                  contentStyle={{
                    fontSize: window.innerWidth < 640 ? '12px' : '14px',
                  }}
                />
                <Bar
                  dataKey="value"
                  fill="#000"
                  radius={[3, 3, 0, 0]}
                  maxBarSize={window.innerWidth < 640 ? 20 : 40}
                />
              </BarChart>
            </ChartContainer>
          </div>
        </Card>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 sm:gap-6">
          {/* Summary Cards */}
          <div className="xl:col-span-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-3 sm:gap-4">
              {summaryData.map((item, idx) => (
                <SummaryBox key={idx} {...item} />
              ))}
            </div>
          </div>

          {/* Top Programs Section */}
          {summary?.topPrograms && summary.topPrograms.length > 0 && (
            <div className="xl:col-span-7">
              <Card className="p-4 sm:p-6 h-full">
                <h3 className="font-semibold text-base sm:text-lg mb-4">
                  โปรแกรมยอดนิยม
                </h3>
                <div className="space-y-3">
                  {summary.topPrograms.slice(0, 5).map((program) => (
                    <div
                      key={program.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg gap-2 sm:gap-4"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm sm:text-base truncate sm:line-clamp-1">
                          {program.title}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                          ดูแล้ว {program.viewCount.toLocaleString()} ครั้ง
                        </div>
                      </div>
                      <div className="flex-shrink-0 self-start sm:self-center">
                        <div
                          className={`px-2 py-1 rounded-full text-xs inline-block ${
                            program.status === 'live'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          }`}
                        >
                          {program.status === 'live'
                            ? 'กำลังถ่ายทอด'
                            : program.status}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </div>
      </LoadingSection>
    </div>
  )
}
